import { flow, isNil, isNull, isString, noop } from 'lodash-es';
import type { ChangeEvent, ElementType, ReactElement } from 'react';
import { useState } from 'react';
import { Form } from 'react-final-form';
import type { WithTranslation } from 'react-i18next';

import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon, SvgIcon } from '@mui/material';

import { ReactComponent as SaveIcon } from 'img/save.svg';

import { CIMixedCheckBox } from 'common/viewcomp/forms/fields/checkbox.mixed';

import withTranslate from '../../i18n/i18n';
import { CIPrimaryButton, CITertiaryButton } from '../../viewcomp/forms/buttons';

import './ci-non-route-block.scss';
export interface DialogParts {
    title?: string;
    message?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    icon?: ElementType;
}

interface CIBlockProps extends WithTranslation {
    showBlock?: boolean;
    stayCallback: () => void;
    goCallback: () => void;
    onSubmit: (values: object) => void;
    dialogParts?: DialogParts;
    className: string;
    isLoading?: boolean;
    userConfirmationRequired?: boolean;
    userConfirmationText?: string;
}
const _CIBlock = ({
    showBlock = false,
    dialogParts,
    stayCallback,
    goCallback,
    className,
    t,
    isLoading,
    userConfirmationRequired,
    userConfirmationText,
}: CIBlockProps): ReactElement => {
    const { title, message, confirmButtonText, cancelButtonText, icon } = dialogParts || {};

    const [isDisabled, setDisabled] = useState(userConfirmationRequired);

    const handleCancelNavigationClick = (_: unknown, reason: 'backdropClick' | 'escapeKeyDown'): boolean | void => {
        if (reason === 'backdropClick') {
            return false;
        }
        setDisabled(userConfirmationRequired);
        stayCallback();
    };

    const handleConfirmNavigationClick = (): void => {
        goCallback();
    };

    const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setDisabled(!e.target.checked && !isDisabled);
    };

    return (
        <Dialog
            open={showBlock}
            onClose={handleCancelNavigationClick}
            className={`ci_prompt_alert_dialog new_ci_prompt ${className || ''} ${
                isNil(message) && 'disbaled-dialogContent'
            }`}
        >
            {!isNil(icon) ? (
                <div className="alert-icon-container">
                    {isString(icon) ? (
                        <Icon className="alert-icon">
                            <img src={icon} alt="" />
                        </Icon>
                    ) : (
                        <SvgIcon className="alert-icon" component={icon} viewBox="0 0 30 30" />
                    )}
                </div>
            ) : (
                <div className="alert-icon-container">
                    <SvgIcon className="alert-icon" component={SaveIcon} viewBox="0 0 30 30" />
                </div>
            )}
            <DialogTitle id="alert-dialog-title" className="alert_dialog_title">
                {isNil(title) ? t('navigation.save_changes') : title}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" className="alert_dialog_description">
                    {userConfirmationRequired ? (
                        <Form
                            onSubmit={noop}
                            initialValues={{
                                isSubmitConfirm: false,
                            }}
                            render={(): ReactElement => {
                                return (
                                    <>
                                        <CIMixedCheckBox
                                            t={t}
                                            id="isSubmitConfirm"
                                            name="isSubmitConfirm"
                                            labelI18NKey={isNil(userConfirmationText) ? '' : userConfirmationText}
                                            onChange={onCheckboxChange}
                                        />
                                    </>
                                );
                            }}
                        />
                    ) : (
                        <>{!isNil(message) && message}</>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions className="alert_dialog_actions">
                <CITertiaryButton
                    onClick={() => handleCancelNavigationClick(null, 'escapeKeyDown')}
                    isLoading={isLoading}
                    className="alert_dialog_actions_cancel_btn"
                >
                    {cancelButtonText || t('navigation.keep_editing')}
                </CITertiaryButton>
                {!isNull(confirmButtonText) && (
                    <CIPrimaryButton
                        onClick={handleConfirmNavigationClick}
                        isLoading={isLoading}
                        disabled={userConfirmationRequired && isDisabled}
                    >
                        {confirmButtonText || t('navigation.discard_changes')}
                    </CIPrimaryButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export const CIBlock = flow([withTranslate('global')])(_CIBlock);
